import {useEffect, useRef, useState} from 'react'
import {EXAM_SCREEN_TRACKING, PATH} from '@utils/Const'
import {useLocation, useNavigate} from 'react-router-dom'
import {
  EXAM_BODY_TYPE,
  EXAM_MODE_TYPE,
  ExamPartType,
  ITestPart,
} from '@pages/ExamPage/examPageModel'
import examRequest from '@apis/exam'
import {isParamsValid, trackingEvent, trackingPage} from '@utils'

function useExamRoom() {
  const navigate = useNavigate()
  const location = useLocation()
  const [data, setData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isLoadingSendAudio, setIsLoadingSendAudio] = useState<boolean>(false)
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [testPart, setTestPart] = useState<ITestPart>()
  const [questionList, setQuestionList] = useState<any[]>([])
  const [showQuestion, setShowQuestion] = useState<number>(0)
  const [showBodyType, setShowBodyType] = useState('')
  const [isShowBtnContinue, setIsShowBtnContinue] = useState<boolean>(false) // nút tiếp tục
  const [isShowAnswer, setIsShowAnswer] = useState<boolean>(false) // modal text câu trả lời part 2
  const [answerData, setAnswerData] = useState<any>() // Text câu trả lời part 2
  const [numberRecordingError, setNumberRecordingError] = useState<number>(0) // số lần thu âm lỗi
  const [isRecordingError, setIsRecordingError] = useState<boolean>(false) // số lần thu âm lỗi

  const countdownTimerRef: any = useRef(null)
  const countdownTimerPart2Ref: any = useRef(null)
  const audioRef: any = useRef(null)
  const examMicRef: any = useRef(null)
  const {testId, type, task_id, entry_point, feature_name} =
    location.state || {}

  useEffect(() => {
    if (!isParamsValid([testId])) {
      navigate(PATH.HOME)
    }
  }, [])

  const handleStart = (bodyType: string) => {
    if (bodyType === EXAM_BODY_TYPE.QUESTION) {
      audioRef.current = new Audio(questionList[showQuestion]?.audio)
      audioRef.current
        .play()
        .catch(() => {
          handleAudioError()
        })
      audioRef.current.addEventListener('ended', handleAudioEnded)
      audioRef.current.addEventListener('error', handleAudioError)
      trackingPage(EXAM_SCREEN_TRACKING.ANSWER_QUESTION_EXAM)
      trackingMultipleEvents(EXAM_SCREEN_TRACKING.ANSWER_QUESTION_EXAM, {
        question_name: questionList[showQuestion]?.question,
      })
    }
    if (bodyType === EXAM_BODY_TYPE.QUESTION_LIST) {
      trackingPage(EXAM_SCREEN_TRACKING.QUESTION_LIST_EXAM)
      trackingMultipleEvents(EXAM_SCREEN_TRACKING.QUESTION_LIST_EXAM)
    }
    setShowBodyType(bodyType)
    return () => {
      audioRef.current.removeEventListener('ended', handleAudioEnded)
      audioRef.current.removeEventListener('error', handleAudioError)
    }
  }
  const handleSaveAudio = async (blob: any) => {
    setIsLoadingSendAudio(true)
    try {
      let file = new File([blob], 'audio.wav', {type: 'audio/wav'})
      const formData = new FormData()
      formData.append('session_id', data?.session_id)
      formData.append('question', questionList[showQuestion]?.question)
      formData.append('part_type', testPart?.type || '')
      formData.append('audio_file', file)
      const response = await examRequest.sendAudioExam(formData)
      setAnswerData(response?.data?.data)
      setIsRecordingError(false)
      setNumberRecordingError(0)
    } catch (error) {
      setIsRecordingError(true)
      setNumberRecordingError((pre: any) => pre + 1)
    }
    setIsLoadingSendAudio(false)
    setIsShowAnswer(true)
  }

  const handleSendAnswer = async () => {
    setIsShowAnswer(false)
    if (showQuestion + 1 < questionList.length) {
      handleContinue()
    } else {
      setIsShowBtnContinue(true)
    }
    if (
      !Boolean(
        currentStep + 1 < data?.test_parts?.length ||
          showQuestion + 1 < questionList.length,
      )
    ) {
      setIsLoadingSendAudio(true)
    }
    const is_last_question: any = !Boolean(
      currentStep + 1 < data?.test_parts?.length ||
        showQuestion + 1 < questionList.length,
    )
    const formData = new FormData()
    formData.append('answer_id', answerData?.answer_id)
    formData.append('session_id', data?.session_id)
    formData.append('is_last_question', is_last_question)
    const res = await examRequest.sendDataAnswer(formData)
    setIsLoadingSendAudio(false)
  }

  const handleSendComBackRecord = () => {
    setIsShowAnswer(false)
    setIsRecordingError(false)
    setTimeout(() => {
      examMicRef.current?.handlePressMic()
    }, 100)

    setTimeout(() => {
      examMicRef.current?.handleEnableMic()
    }, 100)
  }

  const handleContinue = () => {
    if (showQuestion + 1 < questionList.length) {
      setShowQuestion(prev => prev + 1)
      audioRef.current = new Audio(questionList[showQuestion + 1]?.audio)
      audioRef.current
        .play()
        .catch(() => {
          handleAudioError()
        })
      audioRef.current.addEventListener('ended', handleAudioEnded)

      //tracking
      trackingPage(EXAM_SCREEN_TRACKING.ANSWER_QUESTION_EXAM)
      trackingMultipleEvents(EXAM_SCREEN_TRACKING.ANSWER_QUESTION_EXAM, {
        question_name: questionList[showQuestion + 1]?.question,
      })
    } else if (currentStep + 1 < data?.test_parts?.length) {
      const updateTestParts = data?.test_parts[currentStep + 1]
      setTestPart(updateTestParts)
      setQuestionList(updateTestParts?.questions)
      setCurrentStep(prevState => prevState + 1)
      setShowQuestion(0)
      setShowBodyType(EXAM_BODY_TYPE.DESCRIBE)

      //tracking
      trackingPage(EXAM_SCREEN_TRACKING.GUILD_EXAM)
      trackingMultipleEvents(
        EXAM_SCREEN_TRACKING.GUILD_EXAM,
        {},
        updateTestParts,
      )
    } else {
      if (entry_point === 'ROADMAP') {
        navigate(PATH.ROADMAP_SUMMARY, {
          state: {
            task_id,
            feature_name,
          },
        })
      } else {
        navigate(PATH.EXAM_HISTORY)
      }
    }
    setIsShowBtnContinue(false)
    return () => {
      audioRef.current.removeEventListener('ended', handleAudioEnded)
      audioRef.current.removeEventListener('error', handleAudioError)
    }
  }

  const fetchingExamRoom = async () => {
    if (!testId) return
    const res = await examRequest.initExam({
      test_id: testId,
      type: type,
    })
    if (res?.data) {
      setData(res.data?.data)
      setTestPart(res.data?.data?.test_parts[0])
      setQuestionList(res.data?.data?.test_parts[0]?.questions)
      setIsLoading(false)
      const trackingProperties = {
        lesson_id: testId,
        part_name: res.data?.data?.test_parts[0]?.type,
        topic_name: res.data?.data?.test_name,
        topic_id: res.data?.data?.test_parts[0]?.test_part_id,
        practice_type: type,
      }
      if (data?.test_set) {
        trackingEvent(EXAM_SCREEN_TRACKING.GUILD_EXAM, {
          lesson_parent: 'exam',
          exam_set_name: res.data?.data?.test_set?.set_name,
          exam_set_id: res.data?.data?.test_set?.set_id,
          ...trackingProperties,
        })
      } else {
        trackingEvent(EXAM_SCREEN_TRACKING.GUILD_EXAM, {
          lesson_parent: 'topic',
          ...trackingProperties,
        })
      }
    }
  }

  const handleAudioEnded = () => {
    if (type === EXAM_MODE_TYPE.PRACTICE) examMicRef.current?.handleEnableMic()
    if (testPart?.type === ExamPartType.PART_TWO) {
      countdownTimerPart2Ref.current?.startTimer()
      return
    }
    handleTimePrepare()
  }

  const handleAudioError = () => {
    // Khi audio lỗi, vẫn cần mở mic và tiếp tục flow như khi audio phát xong
    handleAudioEnded()
  }

  const handleTimeEnded = async () => {
    const recordingStatus = examMicRef.current?.getMicStatus()
    if (type === EXAM_MODE_TYPE.PRACTICE && !recordingStatus) {
      return
    }
    examMicRef.current?.handlePressMic()
  }

  const handleTimePrepare = () => {
    if (type === EXAM_MODE_TYPE.PRACTICE) return
    examMicRef.current?.handlePressMic()
  }

  const trackingMultipleEvents = (
    eventName: string,
    addProperty?: any,
    updateTestParts?: any,
  ) => {
    const trackingProperties = {
      lesson_id: testId,
      part_name: testPart?.type,
      topic_name: data?.test_name,
      topic_id: testPart?.test_part_id,
      practice_type: type,
      ...addProperty,
    }
    if (data?.test_set) {
      trackingEvent(eventName, {
        lesson_parent: 'exam',
        exam_set_name: data?.test_set?.set_name,
        exam_set_id: data?.test_set?.set_id,
        ...trackingProperties,
      })
    } else {
      trackingEvent(eventName, {
        lesson_parent: 'topic',
        ...trackingProperties,
      })
    }
  }

  useEffect(() => {
    fetchingExamRoom().finally()
    setShowBodyType(EXAM_BODY_TYPE.DESCRIBE)
    trackingPage(EXAM_SCREEN_TRACKING.GUILD_EXAM)
  }, [])

  return {
    data: data,
    currentStep: currentStep,
    testPart: testPart,
    questionList: questionList,
    showQuestion: showQuestion,
    showBodyType: showBodyType,
    isShowBtnContinue: isShowBtnContinue,
    isShowAnswer: isShowAnswer,
    answerData,
    isLoading,
    isLoadingSendAudio,
    audioRef,
    countdownTimerRef,
    countdownTimerPart2Ref,
    examMicRef,
    numberRecordingError,
    isRecordingError,
    handleStart,
    handleSaveAudio,
    handleContinue,
    handleTimeEnded,
    handleTimePrepare,
    handleSendAnswer,
    handleSendComBackRecord,
  }
}

export default useExamRoom
